// Transient board state: the in-progress stroke, the view matrix and the selection overlay.
// Vanilla zustand on purpose - no React component may subscribe to this, it changes every frame.
// Read it with getState() inside the rAF loop, write it from the input pipeline.
// The one exception is the zoom popover, which subscribes only while it is open.

import { createStore } from 'zustand/vanilla';
import { zoomViewAt, type Bounds, type Stroke, type TextObject, type ViewTransform } from '@/whiteboard/model';

export const MIN_SCALE = 0.25;
export const MAX_SCALE = 4;

/** Everything drawn on the composite pass, above the baked and live layers. */
export interface OverlayState {
  /** Marquee rectangle in board space while the select tool is dragging. */
  marquee: Bounds | null;
  /** Bounding box of the current selection, in board space. */
  selectionBox: Bounds | null;
  /** Selected content lifted out of the baked layer for the duration of a move. */
  movingStrokes: Stroke[] | null;
  movingTexts: TextObject[] | null;
  /** Board-space offset applied to the moving content. */
  dx: number;
  dy: number;
  /** Ids the rebake must skip while they are being moved on the overlay. */
  hidden: { [id: string]: true } | null;
}

export interface LiveState {
  view: ViewTransform;
  activeStroke: Stroke | null;
  overlay: OverlayState;
}

const EMPTY_OVERLAY: OverlayState = {
  marquee: null,
  selectionBox: null,
  movingStrokes: null,
  movingTexts: null,
  dx: 0,
  dy: 0,
  hidden: null,
};

export const liveStore = createStore<LiveState>(() => ({
  view: { x: 0, y: 0, scale: 1 },
  activeStroke: null,
  overlay: EMPTY_OVERLAY,
}));

// The renderer is owned by WhiteboardPage; view edits made from the toolbar need a rebake
// and have no other way to reach it. One registered callback beats a per-frame subscription.
let rebakeHandler: (() => void) | null = null;

export function setRebakeHandler(fn: (() => void) | null): void {
  rebakeHandler = fn;
}

export function requestRebake(): void {
  if (rebakeHandler) rebakeHandler();
}

export function getView(): ViewTransform {
  return liveStore.getState().view;
}

export function setView(view: ViewTransform): void {
  liveStore.setState({ view });
}

/** Pan by a screen-space delta - the 60fps path during a pinch or 3-finger gesture. */
export function panViewBy(dx: number, dy: number): void {
  const v = liveStore.getState().view;
  liveStore.setState({ view: { x: v.x + dx, y: v.y + dy, scale: v.scale } });
}

/** Zoom around a screen-space anchor. 60fps path - the caller schedules the frame. */
export function zoomViewBy(factor: number, sx: number, sy: number): void {
  const v = liveStore.getState().view;
  liveStore.setState({ view: zoomViewAt(v, factor, sx, sy, MIN_SCALE, MAX_SCALE) });
}

/** Zoom from the toolbar - low frequency, so rebake straight away for a crisp result. */
export function zoomViewCommitted(factor: number, sx: number, sy: number): void {
  zoomViewBy(factor, sx, sy);
  requestRebake();
}

export function resetView(): void {
  liveStore.setState({ view: { x: 0, y: 0, scale: 1 } });
  requestRebake();
}

export function getActiveStroke(): Stroke | null {
  return liveStore.getState().activeStroke;
}

export function setActiveStroke(stroke: Stroke | null): void {
  liveStore.setState({ activeStroke: stroke });
}

// --- selection overlay --------------------------------------------------------

export function getOverlay(): OverlayState {
  return liveStore.getState().overlay;
}

function patchOverlay(patch: Partial<OverlayState>): void {
  const o = liveStore.getState().overlay;
  liveStore.setState({ overlay: Object.assign({}, o, patch) });
}

export function setMarquee(rect: Bounds | null): void {
  patchOverlay({ marquee: rect });
}

export function setSelectionBox(box: Bounds | null): void {
  patchOverlay({ selectionBox: box });
}

/** Lift the selection onto the overlay so the move can run without touching the baked layer. */
export function beginSelectionMove(
  strokes: Stroke[],
  texts: TextObject[],
  hidden: { [id: string]: true },
): void {
  patchOverlay({ movingStrokes: strokes, movingTexts: texts, hidden, dx: 0, dy: 0 });
}

export function setSelectionOffset(dx: number, dy: number): void {
  patchOverlay({ dx, dy });
}

export function endSelectionMove(): void {
  patchOverlay({ movingStrokes: null, movingTexts: null, hidden: null, dx: 0, dy: 0 });
}

export function resetOverlay(): void {
  liveStore.setState({ overlay: EMPTY_OVERLAY });
}
